<template>
  <div class="escaner-page">
    <!-- Cabecera -->
    <div class="esc-header mb-2">
      <div class="d-flex align-items-center">
        <div class="esc-header__icon">
          <i class="fa-solid fa-barcode"></i>
        </div>
        <div>
          <h2 class="mb-0 font-weight-bolder">Escáner de pedido</h2>
          <small class="text-muted">Escanea · elige color · precio WSP</small>
        </div>
      </div>
      <div class="d-flex align-items-center" style="gap: 0.75rem">
        <b-button-group size="sm" class="esc-moneda">
          <b-button
            v-for="m in monedas"
            :key="m.code"
            :variant="moneda === m.code ? 'primary' : 'outline-primary'"
            @click="moneda = m.code"
          >
            {{ m.symbol }} {{ m.code }}
          </b-button>
        </b-button-group>
        <b-button
          size="sm"
          :variant="mostrarRRP ? 'primary' : 'outline-primary'"
          @click="mostrarRRP = !mostrarRRP"
        >
          <i class="fa-solid fa-tag mr-25"></i> RRP
        </b-button>
        <b-badge variant="light-primary" class="esc-total-badge">
          {{ totalUnidades }} uds · {{ simbolo }}{{ totalPrecio }}
        </b-badge>
      </div>
    </div>

    <b-row>
      <!-- ===================== Escaneo + producto + carrito ===================== -->
      <b-col lg="7">
        <!-- Escaneo -->
        <b-card no-body class="mb-2">
          <b-card-body>
            <b-input-group class="esc-scan-input mb-1">
              <b-input-group-prepend is-text>
                <i class="fa-solid fa-barcode text-muted"></i>
              </b-input-group-prepend>
              <b-form-input
                ref="inputBarcode"
                v-model="codigo"
                type="text"
                autocomplete="off"
                placeholder="Escanea o escribe parte del SKU y pulsa Enter"
                @keyup.enter="buscarManual"
              />
              <b-input-group-append>
                <b-button variant="primary" :disabled="buscando" @click="buscarManual">
                  <b-spinner v-if="buscando" small />
                  <i v-else class="fa-solid fa-magnifying-glass"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <b-button
              variant="outline-primary"
              block
              @click="abrirCamara"
            >
              <i class="fa-solid fa-camera"></i>
              Escanear con cámara
            </b-button>

            <b-button
              variant="flat-primary"
              block
              class="mt-50"
              @click="abrirProductoManual"
            >
              <i class="fa-solid fa-plus mr-50"></i>
              Añadir producto manual
            </b-button>
          </b-card-body>
        </b-card>

        <!-- Ficha del producto escaneado -->
        <b-card v-if="producto" no-body class="mb-2 esc-product-card">
          <div class="esc-card-head">
            <span class="font-weight-bold text-truncate">
              <i class="fa-solid fa-box-open mr-50 text-primary"></i>
              {{ producto.title }}
            </span>
            <b-button size="sm" variant="flat-secondary" class="btn-icon" @click="producto = null">
              <i class="fa-solid fa-xmark"></i>
            </b-button>
          </div>

          <b-card-body>
            <b-row>
              <!-- Imagen principal -->
              <b-col cols="12" md="5" class="mb-1 mb-md-0">
                <div class="esc-gallery-main">
                  <img v-if="imagenActiva" :src="imagenActiva" />
                  <i v-else class="fa-solid fa-image text-muted"></i>
                </div>
              </b-col>

              <!-- Variantes / colores -->
              <b-col cols="12" md="7">
                <small class="text-muted font-weight-bold text-uppercase d-block mb-75">
                  Colores ({{ producto.variants.length }})
                </small>
                <div class="esc-variants">
                  <div
                    v-for="vari in producto.variants"
                    :key="vari.sku"
                    class="esc-variant"
                    :class="{ matched: vari.sku === producto.matchedSku }"
                  >
                    <div class="esc-variant-thumb" @click="vari.image && (imagenActiva = vari.image)">
                      <img v-if="vari.image" :src="vari.image" />
                      <i v-else class="fa-solid fa-image text-muted"></i>
                    </div>
                    <div class="esc-variant-info">
                      <div class="esc-variant-color">
                        {{ vari.color }}
                        <b-badge v-if="vari.sku === producto.matchedSku" variant="light-primary" class="ml-25">escaneado</b-badge>
                        <b-badge v-if="!vari.available" variant="light-danger" class="ml-25">sin stock</b-badge>
                      </div>
                      <code class="esc-variant-sku">{{ vari.sku }}</code>
                    </div>
                    <div class="esc-variant-wsp">
                      {{ simbolo }}{{ wspUnidad(vari.price) }}
                      <div v-if="mostrarRRP" class="esc-variant-rrp">RRP {{ simbolo }}{{ rrpUnidad(vari.price) }}</div>
                    </div>
                    <b-button size="sm" variant="primary" class="esc-variant-add" @click="agregarVariante(vari)">
                      <i class="fa-solid fa-plus"></i>
                    </b-button>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <!-- Carrito -->
        <b-card no-body>
          <div class="esc-card-head">
            <span class="font-weight-bold">
              <i class="fa-solid fa-basket-shopping mr-50 text-primary"></i>
              Carrito
              <b-badge v-if="carrito.length" variant="light-primary" pill class="ml-25">{{ carrito.length }}</b-badge>
            </span>
            <b-button v-if="carrito.length" size="sm" variant="flat-danger" @click="vaciar">
              <i class="fa-solid fa-trash-can"></i> Vaciar
            </b-button>
          </div>

          <div v-if="carrito.length === 0" class="esc-empty">
            <i class="fa-solid fa-basket-shopping"></i>
            <p class="mb-0 font-weight-bold">El carrito está vacío</p>
            <small class="text-muted">Escanea un producto y añade colores</small>
          </div>

          <div v-else class="esc-cart-list">
            <div v-for="(item, idx) in carrito" :key="item.sku" class="esc-cart-item">
              <div
                class="esc-thumb"
                :class="{ 'esc-thumb-clickable': item.image }"
                @click="item.image && verImagen(item.image)"
              >
                <img v-if="item.image" :src="item.image" />
                <i v-else class="fa-solid fa-image text-muted"></i>
              </div>
              <div class="esc-info">
                <div class="esc-title">{{ item.title }}</div>
                <span class="esc-color">{{ item.color }}</span>
                <code class="esc-barcode">{{ item.sku }}</code>
              </div>
              <div class="esc-actions">
                <div class="esc-qty">
                  <b-input-group size="sm" class="esc-qty-group">
                    <b-input-group-prepend>
                      <b-button variant="outline-secondary" class="esc-qty-btn" @click="decrementar(item)">
                        <i class="fa-solid fa-minus"></i>
                      </b-button>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="item.qty"
                      type="number"
                      min="1"
                      max="999"
                      class="text-center esc-qty-input"
                      @change="normalizarQty(item)"
                    />
                    <b-input-group-append>
                      <b-button variant="outline-secondary" class="esc-qty-btn" @click="incrementar(item)">
                        <i class="fa-solid fa-plus"></i>
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
                <div class="esc-line">
                  <div class="esc-price">
                    <b-input-group size="sm" class="esc-price-group" :prepend="simbolo">
                      <b-form-input
                        type="number"
                        min="0"
                        step="0.01"
                        class="text-right esc-price-input"
                        :class="{ 'esc-price-manual': esPrecioManual(item) }"
                        :value="wspItem(item)"
                        :title="esPrecioManual(item) ? 'Precio manual (vacía para volver al automático)' : 'Precio unitario'"
                        @change="setPrecioManual(item, $event)"
                      />
                    </b-input-group>
                    <div class="esc-line-total">= {{ simbolo }}{{ lineaTotal(item) }}</div>
                    <b-input-group v-if="mostrarRRP" size="sm" class="esc-price-group" prepend="RRP">
                      <b-form-input
                        type="number"
                        min="0"
                        step="0.01"
                        class="text-right esc-price-input"
                        :class="{ 'esc-price-manual': esRrpManual(item) }"
                        :value="rrpItem(item)"
                        title="RRP unitario (vacía para volver al automático)"
                        @change="setRrpManual(item, $event)"
                      />
                    </b-input-group>
                  </div>
                  <b-button variant="flat-danger" size="sm" class="btn-icon" @click="quitar(idx)">
                    <i class="fa-solid fa-xmark"></i>
                  </b-button>
                </div>
              </div>
            </div>

            <div class="esc-cart-total">
              <span>Subtotal <small class="text-muted">(WSP)</small></span>
              <span class="esc-total-num">{{ simbolo }}{{ subtotalPrecio }}</span>
            </div>
            <div v-if="mostrarRRP" class="esc-cart-total esc-cart-total-rrp">
              <span>Subtotal <small class="text-muted">(RRP)</small></span>
              <span class="esc-total-num">{{ simbolo }}{{ subtotalRRP }}</span>
            </div>
          </div>
        </b-card>
      </b-col>

      <!-- ===================== Datos del cliente ===================== -->
      <b-col lg="5">
        <div class="esc-sticky">
          <b-card no-body>
            <div class="esc-card-head d-flex justify-content-between align-items-center">
              <span class="font-weight-bold">
                <i class="fa-solid fa-user mr-50 text-primary"></i> Datos del cliente
              </span>
              <b-button size="sm" variant="outline-danger" @click="borrarCliente">
                <i class="fa-solid fa-trash mr-50"></i> Borrar
              </b-button>
            </div>
            <b-card-body>
              <b-form-group label="Customer guardado" label-class="small font-weight-bold">
                <v-select
                  v-model="customerSel"
                  :options="customers"
                  placeholder="Buscar customer guardado..."
                  @input="aplicarCustomer"
                />
              </b-form-group>

              <!-- Customer guardado: se usan sus datos, no hace falta rellenar nada -->
              <div v-if="customerSel" class="esc-cliente-resumen">
                <div class="esc-cliente-resumen__nombre">
                  {{ [cliente.nombre, cliente.apellidos].filter(Boolean).join(' ') || cliente.empresa }}
                  <span v-if="cliente.empresa && (cliente.nombre || cliente.apellidos)"> — {{ cliente.empresa }}</span>
                </div>
                <small class="text-muted d-block" v-if="cliente.email || cliente.telefono">
                  {{ [cliente.email, cliente.telefono].filter(Boolean).join(' · ') }}
                </small>
                <small class="text-muted d-block" v-if="cliente.direccion">
                  {{ [cliente.direccion, cliente.cp, cliente.ciudad, cliente.pais].filter(Boolean).join(', ') }}
                </small>
                <small class="text-muted d-block" v-if="envio.activo && envio.direccion">
                  Envío: {{ [envio.direccion, envio.cp, envio.ciudad, envio.pais].filter(Boolean).join(', ') }}
                </small>
              </div>

              <template v-if="!customerSel">
              <b-form-group label="Company name" label-class="small font-weight-bold">
                <b-form-input v-model="cliente.empresa" placeholder="Nombre de la empresa" />
              </b-form-group>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Name" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.nombre" placeholder="Nombre" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Surname" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.apellidos" placeholder="Apellidos" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="NIF" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.nif" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="VAT number" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.vat" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Email" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.email" type="email" placeholder="correo@ejemplo.com" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Teléfono" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.telefono" type="tel" inputmode="tel" placeholder="+44 ..." />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Dirección" label-class="small font-weight-bold">
                <b-form-input v-model="cliente.direccion" placeholder="Calle, número" />
              </b-form-group>
              <b-row>
                <b-col cols="5">
                  <b-form-group label="C.P." label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.cp" />
                  </b-form-group>
                </b-col>
                <b-col cols="7">
                  <b-form-group label="Ciudad" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.ciudad" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="País" label-class="small font-weight-bold">
                <b-form-input v-model="cliente.pais" placeholder="Reino Unido" />
              </b-form-group>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Web" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.web" placeholder="https://..." />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Social media" label-class="small font-weight-bold">
                    <b-form-input v-model="cliente.redes" placeholder="@usuario" />
                  </b-form-group>
                </b-col>
              </b-row>
              </template>

              <b-form-group label="Notas" label-class="small font-weight-bold">
                <b-form-textarea v-model="cliente.notas" rows="2" placeholder="Notas del pedido (salen en el PDF)" />
              </b-form-group>
              <b-form-group label="Observaciones" label-class="small font-weight-bold">
                <b-form-textarea v-model="cliente.observaciones" rows="2" placeholder="Observaciones internas (no salen en el PDF)" />
              </b-form-group>

              <hr v-if="!customerSel" class="my-1" />
              <b-form-checkbox v-if="!customerSel" v-model="envio.activo" switch class="mb-0">
                <span class="font-weight-bold small">La dirección de envío es diferente</span>
              </b-form-checkbox>

              <div v-if="!customerSel && envio.activo" class="mt-1">
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group label="Nombre (envío)" label-class="small font-weight-bold">
                      <b-form-input v-model="envio.nombre" placeholder="Nombre y apellidos" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label="Empresa (envío)" label-class="small font-weight-bold">
                      <b-form-input v-model="envio.empresa" />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-form-group label="Dirección (envío)" label-class="small font-weight-bold">
                  <b-form-input v-model="envio.direccion" placeholder="Calle, número" />
                </b-form-group>
                <b-row>
                  <b-col cols="5">
                    <b-form-group label="C.P." label-class="small font-weight-bold">
                      <b-form-input v-model="envio.cp" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="7">
                    <b-form-group label="Ciudad" label-class="small font-weight-bold">
                      <b-form-input v-model="envio.ciudad" />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12" md="6">
                    <b-form-group label="País" label-class="small font-weight-bold" class="mb-0">
                      <b-form-input v-model="envio.pais" placeholder="Reino Unido" />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="6">
                    <b-form-group label="Teléfono (envío)" label-class="small font-weight-bold" class="mb-0">
                      <b-form-input v-model="envio.telefono" type="tel" inputmode="tel" placeholder="+44 ..." />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </b-card-body>
          </b-card>

          <b-card no-body class="mt-2 esc-summary">
            <b-card-body>
              <div class="d-flex justify-content-between mb-50">
                <span class="text-muted">Artículos</span>
                <span class="font-weight-bold">{{ carrito.length }}</span>
              </div>
              <div class="d-flex justify-content-between mb-50">
                <span class="text-muted">Unidades</span>
                <span class="font-weight-bold">{{ totalUnidades }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center mb-50">
                <span class="text-muted">Subtotal</span>
                <span class="font-weight-bold">{{ simbolo }}{{ subtotalPrecio }}</span>
              </div>
              <div v-if="mostrarRRP" class="d-flex justify-content-between align-items-center mb-50">
                <span class="text-muted">Subtotal (RRP)</span>
                <span class="font-weight-bold text-muted">{{ simbolo }}{{ subtotalRRP }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center mb-50">
                <span class="text-muted">Descuento</span>
                <b-input-group size="sm" append="%" class="esc-desc-input">
                  <b-form-input v-model="descuento" type="number" min="0" max="100" class="text-right" />
                </b-input-group>
              </div>
              <div v-if="descuentoImporte > 0" class="d-flex justify-content-between mb-1 text-danger">
                <span>Descuento ({{ descuentoPct }}%)</span>
                <span class="font-weight-bold">−{{ simbolo }}{{ descuentoPrecio }}</span>
              </div>
              <div class="d-flex justify-content-between align-items-center mb-50">
                <b-form-checkbox v-model="vat0" switch>
                  <span class="text-muted">Mostrar "0% VAT" en los PDF</span>
                </b-form-checkbox>
                <span v-if="vat0" class="font-weight-bold text-muted">{{ simbolo }}0.00</span>
              </div>
              <div class="mb-50">
                <b-form-checkbox v-model="datosBanco" switch>
                  <span class="text-muted">Añadir datos bancarios (Wise EUR) en los PDF</span>
                </b-form-checkbox>
              </div>
              <div class="d-flex justify-content-between align-items-center esc-summary-total mb-1">
                <span class="font-weight-bold">Total (WSP)</span>
                <span class="esc-total-num">{{ simbolo }}{{ totalPrecio }}</span>
              </div>
              <div v-if="mostrarRRP" class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-muted">Total (RRP)</span>
                <span class="font-weight-bold text-muted">{{ simbolo }}{{ totalRRP }}</span>
              </div>
              <!-- Opciones de envío del correo -->
              <b-form-checkbox v-model="enviarCorreo" class="mb-75">
                Enviar correo al cliente
              </b-form-checkbox>
              <b-form-input
                v-model="emailRevision"
                type="email"
                size="sm"
                class="mb-1"
                placeholder="Correo de revisión (opcional)"
              />
              <b-button
                block
                size="lg"
                variant="primary"
                class="esc-finalizar"
                :disabled="carrito.length === 0 || finalizando"
                @click="finalizar"
              >
                <b-spinner v-if="finalizando" small class="mr-50" />
                <i v-else class="fa-solid fa-check mr-50"></i>
                {{ finalizando ? 'Procesando…' : 'Finalizar pedido' }}
              </b-button>
              <b-button
                block
                variant="outline-primary"
                class="mt-1"
                :disabled="carrito.length === 0 || generandoPdf"
                @click="verPdf"
              >
                <b-spinner v-if="generandoPdf" small class="mr-50" />
                <i v-else class="fa-solid fa-file-pdf mr-50"></i>
                {{ generandoPdf ? 'Generando PDF…' : 'Ver PDF' }}
              </b-button>
              <small class="text-muted d-block text-center mt-75">
                Se guarda el pedido<span v-if="emailRevision || (enviarCorreo && cliente.email)"> y se envía a {{ emailRevision || cliente.email }}</span>
              </small>
            </b-card-body>
          </b-card>
        </div>
      </b-col>
    </b-row>

    <!-- Aviso sutil al añadir al carrito -->
    <transition name="esc-snack-fade">
      <div v-if="snackbar.show" class="esc-snack">
        <i class="fa-solid fa-check-circle"></i>
        <span>{{ snackbar.text }}</span>
      </div>
    </transition>

    <!-- Modal visor de PDF -->
    <b-modal
      v-model="mostrarPdf"
      title="Pedido (PDF)"
      size="lg"
      centered
      hide-footer
      body-class="p-0"
      @hide="cerrarPdf"
    >
      <div class="esc-pdf-wrap">
        <iframe v-if="pdfUrl" :src="pdfUrl" class="esc-pdf-frame" title="PDF del pedido"></iframe>
      </div>
      <div class="text-center p-1">
        <b-button variant="primary" @click="descargarPdfArchivo">
          <i class="fa-solid fa-download mr-50"></i> Descargar
        </b-button>
      </div>
    </b-modal>

    <!-- Modal cámara / escaneo -->
    <b-modal
      v-model="mostrarCamara"
      hide-footer
      centered
      title="Escanear código"
      body-class="p-0 esc-cam-modal-body"
      @shown="iniciarCamara"
      @hide="detenerCamara"
    >
      <div class="esc-reader-wrap">
        <div id="esc-reader" ref="reader"></div>
        <div v-if="ultimoEscaneado" class="esc-last-scan">
          <i class="fa-solid fa-check-circle"></i> {{ ultimoEscaneado }}
        </div>
      </div>
      <p class="text-center text-muted small mb-0 py-1">
        Apunta al código de barras
      </p>
    </b-modal>

    <!-- Modal imagen en grande -->
    <b-modal
      v-model="mostrarImagen"
      hide-footer
      hide-header
      centered
      size="lg"
      body-class="p-0 esc-img-modal-body"
    >
      <div class="esc-img-modal" @click="mostrarImagen = false">
        <img v-if="imagenGrande" :src="imagenGrande" />
      </div>
    </b-modal>

    <!-- Modal añadir producto manual -->
    <b-modal
      v-model="mostrarManual"
      centered
      title="Añadir producto manual"
      ok-title="Añadir"
      cancel-title="Cancelar"
      ok-variant="primary"
      @ok="agregarManual"
    >
      <b-form-group label="Título *">
        <b-form-input v-model="manual.title" placeholder="Nombre del producto" autofocus />
      </b-form-group>
      <b-form-group label="Descripción">
        <b-form-input v-model="manual.descripcion" placeholder="Color, material, detalles…" />
      </b-form-group>
      <b-form-group label="SKU">
        <b-form-input v-model="manual.sku" placeholder="Se genera uno si lo dejas vacío" />
      </b-form-group>
      <b-row>
        <b-col cols="6">
          <b-form-group :label="`Precio WSP (${simbolo}) *`">
            <b-form-input v-model="manual.wsp" type="number" min="0" step="0.01" placeholder="0" />
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group :label="`Precio RRP (${simbolo})`">
            <b-form-input v-model="manual.rrp" type="number" min="0" step="0.01" placeholder="0" />
          </b-form-group>
        </b-col>
      </b-row>
      <small class="text-muted">Los precios se guardan en {{ moneda }} para este pedido.</small>
    </b-modal>

  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'

// Redondea a 2 decimales (precios manuales y totales)
const round2 = n => Math.round((Number(n) || 0) * 100) / 100
// Enteros tal cual; con decimales, siempre 2 (12.5 → "12.50")
const fmtPrecio = n => (Number.isInteger(n) ? String(n) : round2(n).toFixed(2))
// Sin decimales se mantiene el redondeo hacia arriba al entero; con decimales, a 2 decimales
const totalConDescuento = (subtotal, pct) => {
  const t = subtotal * (1 - pct / 100)
  return Number.isInteger(subtotal) ? Math.ceil(round2(t)) : round2(t)
}

export default {
  components: { vSelect },
  data() {
    return {
      codigo: '',
      buscando: false,
      producto: null, // { title, images:[], variants:[], matchedSku }
      imagenActiva: '',
      carrito: [], // [{ sku, barcode, title, color, image, wsp, qty }]
      escaneando: false,
      mostrarCamara: false,
      ultimoEscaneado: '',
      html5Qr: null,
      ultimoScan: { code: '', time: 0 },
      cliente: {
        id: null,
        empresa: '', nombre: '', apellidos: '', nif: '', vat: '',
        email: '', telefono: '',
        direccion: '', cp: '', ciudad: '', pais: '',
        web: '', redes: '', notas: '', observaciones: '',
      },
      envio: {
        activo: false,
        empresa: '', nombre: '', telefono: '',
        direccion: '', cp: '', ciudad: '', pais: '',
      },
      customers: [],
      customerSel: null,
      mostrarImagen: false,
      imagenGrande: '',
      mostrarManual: false,
      manual: { title: '', descripcion: '', sku: '', wsp: '', rrp: '' },
      snackbar: { show: false, text: '' },
      snackTimer: null,
      generandoPdf: false,
      finalizando: false,
      mostrarPdf: false,
      pdfUrl: '',
      pdfNombre: 'pedido.pdf',
      moneda: 'GBP',
      mostrarRRP: false,
      // Desmarcado por defecto: el correo al cliente solo se envía si se marca a propósito
      enviarCorreo: false,
      emailRevision: '',
      descuento: 0,
      vat0: false,
      datosBanco: false,
      // Tasas desde GBP (base de la tienda)
      monedas: [
        { code: 'GBP', symbol: '£', rate: 1 },
        { code: 'EUR', symbol: '€', rate: 1.17 },
        { code: 'USD', symbol: '$', rate: 1.35 },
        { code: 'SGD', symbol: 'S$', rate: 1.73 },
      ],
    }
  },
  computed: {
    monedaActual() {
      return this.monedas.find(m => m.code === this.moneda) || this.monedas[0]
    },
    simbolo() {
      return this.monedaActual.symbol
    },
    totalUnidades() {
      return this.carrito.reduce((acc, it) => acc + (Number(it.qty) || 0), 0)
    },
    // Suma de líneas (WSP) sin descuento
    subtotalNum() {
      return round2(this.carrito.reduce((acc, it) => acc + this.wspItem(it) * (Number(it.qty) || 0), 0))
    },
    descuentoPct() {
      let p = parseFloat(this.descuento) || 0
      if (p < 0) p = 0
      if (p > 100) p = 100
      return p
    },
    // Total con descuento: redondeado hacia arriba, salvo que haya precios manuales con decimales
    totalNum() {
      return totalConDescuento(this.subtotalNum, this.descuentoPct)
    },
    descuentoImporte() {
      return round2(this.subtotalNum - this.totalNum)
    },
    subtotalPrecio() {
      return fmtPrecio(this.subtotalNum)
    },
    // Subtotal en RRP (precio de venta), sin descuento
    subtotalRRPNum() {
      return round2(this.carrito.reduce((acc, it) => acc + this.rrpItem(it) * (Number(it.qty) || 0), 0))
    },
    subtotalRRP() {
      return fmtPrecio(this.subtotalRRPNum)
    },
    // Total en RRP con el mismo descuento aplicado, redondeado hacia arriba
    totalRRP() {
      return fmtPrecio(totalConDescuento(this.subtotalRRPNum, this.descuentoPct))
    },
    descuentoPrecio() {
      return fmtPrecio(this.descuentoImporte)
    },
    totalPrecio() {
      return fmtPrecio(this.totalNum)
    },
  },
  created() {
    axios.post('/uso', { seccion: 'escaner' }).catch(() => {})
    axios
      .get('/customers')
      .then(({ data }) => {
        this.customers = (data || []).map(c => ({
          ...c,
          label: [c.nombre, c.apellidos].filter(Boolean).join(' ')
            + (c.companyName ? ` — ${c.companyName}` : ''),
        }))
      })
      .catch(() => {})
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.inputBarcode) this.$refs.inputBarcode.focus()
    })
  },
  beforeDestroy() {
    this.detenerCamara()
    if (this.snackTimer) clearTimeout(this.snackTimer)
    if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl)
  },
  methods: {
    notificar(titulo, variant = 'success') {
      this.$bvToast.toast(titulo, {
        title: variant === 'danger' ? 'Error' : 'Escáner',
        variant,
        solidColor: true,
      })
    },
    // Aviso discreto tipo snackbar (no usa los toasts grandes)
    mostrarSnack(text) {
      this.snackbar.text = text
      this.snackbar.show = true
      if (this.snackTimer) clearTimeout(this.snackTimer)
      this.snackTimer = setTimeout(() => {
        this.snackbar.show = false
      }, 1400)
    },
    // WSP unitario (40% del RRP, es decir 60% de descuento) en la moneda seleccionada, redondeado arriba
    wspUnidad(priceGBP) {
      const base = (parseFloat(priceGBP) || 0) * 0.4 * this.monedaActual.rate
      return Math.ceil(base)
    },
    // WSP unitario de un item: usa el precio manual si se fijó para esta moneda, si no el calculado
    wspItem(item) {
      const m = item.wspManual && item.wspManual[this.moneda]
      return (m != null && m !== '') ? m : this.wspUnidad(item.price)
    },
    // Fija (o limpia, si val vacío/0) el precio manual del item para la moneda actual
    setPrecioManual(item, val) {
      if (!item.wspManual) this.$set(item, 'wspManual', {})
      const n = parseFloat(val)
      if (!n || n < 0) this.$delete(item.wspManual, this.moneda)
      else this.$set(item.wspManual, this.moneda, round2(n))
    },
    esPrecioManual(item) {
      return !!(item.wspManual && item.wspManual[this.moneda] != null)
    },
    // RRP unitario de un item: usa el manual si se fijó para esta moneda, si no el calculado
    rrpItem(item) {
      const m = item.rrpManual && item.rrpManual[this.moneda]
      return (m != null && m !== '') ? m : this.rrpUnidad(item.price)
    },
    setRrpManual(item, val) {
      if (!item.rrpManual) this.$set(item, 'rrpManual', {})
      const n = parseFloat(val)
      if (!n || n < 0) this.$delete(item.rrpManual, this.moneda)
      else this.$set(item.rrpManual, this.moneda, round2(n))
    },
    esRrpManual(item) {
      return !!(item.rrpManual && item.rrpManual[this.moneda] != null)
    },
    // RRP unitario (precio de venta) en la moneda seleccionada, redondeado arriba
    rrpUnidad(priceGBP) {
      const base = (parseFloat(priceGBP) || 0) * this.monedaActual.rate
      return Math.ceil(base)
    },
    lineaTotal(item) {
      return fmtPrecio(round2(this.wspItem(item) * (Number(item.qty) || 0)))
    },
    lineaTotalRRP(item) {
      return fmtPrecio(round2(this.rrpItem(item) * (Number(item.qty) || 0)))
    },
    buscarManual() {
      const code = (this.codigo || '').trim()
      if (!code) return
      this.buscarTexto(code)
      this.codigo = ''
      if (this.$refs.inputBarcode) this.$refs.inputBarcode.focus()
    },
    // Búsqueda por subcadena: muestra todas las coincidencias del SKU/barcode.
    buscarTexto(q) {
      this.buscando = true
      if (navigator.vibrate) navigator.vibrate(60)
      axios
        .get('/escaner/search', { params: { q } })
        .then(({ data }) => {
          const results = (data && data.results) || []
          if (results.length === 0) {
            this.notificar('Sin coincidencias para: ' + q, 'danger')
            return
          }
          // Siempre abre la ficha del producto (con todos sus colores).
          // Prioriza la coincidencia exacta de SKU/barcode; si no, el primer resultado.
          const ql = q.toLowerCase()
          const elegido = results.find(r => (r.sku || '').toLowerCase() === ql || r.barcode === q) || results[0]
          this.buscarProducto(elegido.sku)
        })
        .catch(() => {
          this.notificar('No se pudo consultar Shopify', 'danger')
        })
        .finally(() => {
          this.buscando = false
        })
    },
    buscarProducto(code) {
      this.buscando = true
      if (navigator.vibrate) navigator.vibrate(60)
      axios
        .get('/escaner/product', { params: { code } })
        .then(({ data }) => {
          if (!data || !data.found) {
            this.notificar('Producto no encontrado: ' + code, 'danger')
            return
          }
          this.producto = data
          // Imagen grande = la del color escaneado (variante que coincide)
          const variEscaneada = (data.variants || []).find(v => v.sku === data.matchedSku)
          this.imagenActiva = (variEscaneada && variEscaneada.image)
            || (data.images && data.images[0])
            || ''
        })
        .catch(() => {
          this.notificar('No se pudo consultar Shopify', 'danger')
        })
        .finally(() => {
          this.buscando = false
        })
    },
    agregarVariante(vari) {
      const existente = this.carrito.find(it => it.sku === vari.sku)
      if (existente) {
        existente.qty = (Number(existente.qty) || 1) + 1
      } else {
        this.carrito.unshift({
          sku: vari.sku,
          barcode: vari.barcode,
          title: this.producto.title,
          color: vari.color,
          image: vari.image,
          price: vari.price, // RRP en GBP (base para convertir a la moneda elegida)
          qty: 1,
          wspManual: {}, // WSP unitario manual por moneda { EUR: 250, ... }
          rrpManual: {}, // RRP unitario manual por moneda
        })
      }
      this.mostrarSnack(`${vari.color} añadido`)
    },
    abrirProductoManual() {
      this.manual = { title: '', descripcion: '', sku: '', wsp: '', rrp: '' }
      this.mostrarManual = true
    },
    agregarManual(bvModalEvt) {
      const title = (this.manual.title || '').trim()
      const wspVal = parseFloat(this.manual.wsp) || 0
      const rrpVal = parseFloat(this.manual.rrp) || 0
      if (!title || wspVal <= 0) {
        if (bvModalEvt) bvModalEvt.preventDefault()
        this.notificar('Indica al menos título y precio WSP', 'danger')
        return
      }
      const rate = this.monedaActual.rate
      // Base RRP en GBP para que el item se convierta a otras monedas si se cambia de divisa
      const priceGBP = rrpVal > 0 ? rrpVal / rate : wspVal / 0.4 / rate

      // SKU único (el indicado o uno generado); evita choque con otro del carrito
      let sku = (this.manual.sku || '').trim() || `MANUAL-${Date.now()}`
      while (this.carrito.some(it => it.sku === sku)) sku += '-1'

      this.carrito.unshift({
        sku,
        barcode: '',
        title,
        color: (this.manual.descripcion || '').trim(),
        image: '',
        price: priceGBP,
        qty: 1,
        wspManual: { [this.moneda]: round2(wspVal) },
        rrpManual: rrpVal > 0 ? { [this.moneda]: round2(rrpVal) } : {},
        manual: true,
      })
      this.mostrarManual = false
      this.mostrarSnack(`${title} añadido`)
    },
    incrementar(item) {
      item.qty = Math.min(999, (parseInt(item.qty, 10) || 0) + 1)
    },
    decrementar(item) {
      item.qty = Math.max(1, (parseInt(item.qty, 10) || 1) - 1)
    },
    normalizarQty(item) {
      let n = parseInt(item.qty, 10)
      if (!n || n < 1) n = 1
      if (n > 999) n = 999
      item.qty = n
    },
    verImagen(url) {
      this.imagenGrande = url
      this.mostrarImagen = true
    },
    quitar(idx) {
      this.carrito.splice(idx, 1)
    },
    vaciar() {
      if (this.carrito.length === 0) return
      this.$bvModal
        .msgBoxConfirm('¿Seguro que quieres vaciar el carrito?', {
          title: 'Vaciar carrito',
          okVariant: 'danger',
          okTitle: 'Vaciar',
          cancelTitle: 'Cancelar',
          centered: true,
        })
        .then((ok) => {
          if (ok) this.carrito = []
        })
    },
    // Rellena el formulario con un customer guardado y lo asocia al pedido (cliente.id).
    // Al quitar la selección (x del combo) se limpia el formulario del cliente
    // (las notas/observaciones son del pedido y se conservan).
    aplicarCustomer(c) {
      if (!c) {
        this.cliente = {
          id: null,
          empresa: '', nombre: '', apellidos: '', nif: '', vat: '',
          email: '', telefono: '',
          direccion: '', cp: '', ciudad: '', pais: '',
          web: '', redes: '',
          notas: this.cliente.notas,
          observaciones: this.cliente.observaciones,
        }
        this.envio = {
          activo: false,
          empresa: '', nombre: '', telefono: '',
          direccion: '', cp: '', ciudad: '', pais: '',
        }
        return
      }
      this.cliente = {
        id: c.id,
        empresa: c.companyName || '',
        nombre: c.nombre || '',
        apellidos: c.apellidos || '',
        nif: c.nif || '',
        vat: c.vat || '',
        email: c.email || '',
        telefono: c.telefono || '',
        direccion: c.direccion || '',
        cp: c.cp || '',
        ciudad: c.ciudad || '',
        pais: c.pais || '',
        web: c.web || '',
        redes: c.redes || '',
        notas: this.cliente.notas,
        observaciones: this.cliente.observaciones,
      }
      this.envio = {
        activo: c.envioActivo == 1,
        empresa: c.envioEmpresa || '',
        nombre: c.envioNombre || '',
        telefono: c.envioTelefono || '',
        direccion: c.envioDireccion || '',
        cp: c.envioCp || '',
        ciudad: c.envioCiudad || '',
        pais: c.envioPais || '',
      }
    },
    borrarCliente() {
      this.$bvModal
        .msgBoxConfirm('¿Seguro que quieres borrar los datos del cliente?', {
          title: 'Borrar cliente',
          okVariant: 'danger',
          okTitle: 'Borrar',
          cancelTitle: 'Cancelar',
          centered: true,
        })
        .then((ok) => {
          if (!ok) return
          this.customerSel = null
          this.cliente = {
            id: null,
            empresa: '', nombre: '', apellidos: '', nif: '', vat: '',
            email: '', telefono: '',
            direccion: '', cp: '', ciudad: '', pais: '',
            web: '', redes: '', notas: '', observaciones: '',
          }
          this.envio = {
            activo: false,
            empresa: '', nombre: '', telefono: '',
            direccion: '', cp: '', ciudad: '', pais: '',
          }
        })
    },
    // ---- Cámara ----
    abrirCamara() {
      this.ultimoEscaneado = ''
      this.mostrarCamara = true // al mostrarse (@shown) se inicia la cámara
    },
    iniciarCamara() {
      if (this.html5Qr) return
      this.escaneando = true
      this.html5Qr = new Html5Qrcode('esc-reader', {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
        ],
        // Usa el detector de códigos nativo del navegador si está disponible
        // (mucho más rápido y preciso, sobre todo en Android Chrome)
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        verbose: false,
      })
      // Caja de lectura ancha: los códigos de barras son apaisados
      const qrbox = (vw, vh) => {
        const ancho = Math.floor(Math.min(vw * 0.9, 280))
        return { width: ancho, height: Math.max(110, Math.floor(ancho * 0.5)) }
      }
      this.html5Qr
        .start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox,
            // Alta resolución + enfoque continuo para leer códigos pequeños
            videoConstraints: {
              facingMode: 'environment',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              advanced: [{ focusMode: 'continuous' }],
            },
          },
          (decoded) => this.onScan(decoded),
          () => {},
        )
        .then(() => this.aplicarEnfoqueContinuo())
        .catch((err) => {
          this.escaneando = false
          this.html5Qr = null
          this.mostrarCamara = false
          this.notificar('No se pudo abrir la cámara: ' + err, 'danger')
        })
    },
    // Fuerza el enfoque continuo si el dispositivo lo soporta
    aplicarEnfoqueContinuo() {
      try {
        if (!this.html5Qr || !this.html5Qr.getRunningTrackCapabilities) return
        const caps = this.html5Qr.getRunningTrackCapabilities()
        if (caps && Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
          this.html5Qr
            .applyVideoConstraints({ advanced: [{ focusMode: 'continuous' }] })
            .catch(() => {})
        }
      } catch (e) {
        // silencioso si el navegador no expone estas capacidades
      }
    },
    detenerCamara() {
      this.escaneando = false
      if (this.html5Qr) {
        const qr = this.html5Qr
        this.html5Qr = null
        qr.stop().then(() => qr.clear()).catch(() => {})
      }
    },
    // Beep corto al detectar un código (sin archivo de audio)
    reproducirBeep() {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext
        if (!Ctx) return
        const ctx = new Ctx()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'square'
        osc.frequency.value = 880
        gain.gain.setValueAtTime(0.15, ctx.currentTime)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.15)
        osc.onended = () => ctx.close()
      } catch (e) {
        // silencioso si el navegador bloquea el audio
      }
    },
    onScan(decoded) {
      const now = Date.now()
      if (decoded === this.ultimoScan.code && now - this.ultimoScan.time < 1500) return
      this.ultimoScan = { code: decoded, time: now }
      this.ultimoEscaneado = decoded
      this.reproducirBeep()
      if (navigator.vibrate) navigator.vibrate(80)
      this.mostrarCamara = false // cierra el modal → @hide detiene la cámara
      this.buscarProducto(decoded)
    },
    construirPayloadPedido() {
      return {
        cliente: this.cliente,
        envio: this.envio,
        moneda: this.moneda,
        simbolo: this.simbolo,
        subtotal: this.subtotalPrecio,
        descuentoPct: this.descuentoPct,
        descuento: this.descuentoPrecio,
        vat0: this.vat0,
        datosBanco: this.datosBanco,
        total: this.totalPrecio,
        items: this.carrito.map(it => ({
          title: it.title,
          color: it.color,
          sku: it.sku,
          image: it.image,
          qty: it.qty,
          price: it.price, // RRP en GBP (base, para el Excel mayorista)
          wspUnit: this.wspItem(it),
          wspManual: it.wspManual || null,
          rrpUnit: this.rrpItem(it),
          rrpManual: it.rrpManual || null,
          lineTotal: this.lineaTotal(it),
        })),
      }
    },
    // Genera el PDF y lo muestra en un modal dentro de la app
    verPdf() {
      if (this.carrito.length === 0) return
      this.generandoPdf = true
      const payload = this.construirPayloadPedido()
      axios
        .post('/escaner/pedido/pdf', payload, { responseType: 'blob' })
        .then(({ data }) => {
          if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl)
          this.pdfUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          const nombre = (this.cliente.nombre || 'cliente').trim().replace(/\s+/g, '_').toLowerCase()
          this.pdfNombre = `pedido_${nombre}.pdf`
          this.mostrarPdf = true
        })
        .catch(() => {
          this.notificar('No se pudo generar el PDF', 'danger')
        })
        .finally(() => {
          this.generandoPdf = false
        })
    },
    descargarPdfArchivo() {
      if (!this.pdfUrl) return
      const a = document.createElement('a')
      a.href = this.pdfUrl
      a.download = this.pdfNombre
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    cerrarPdf() {
      if (this.pdfUrl) {
        URL.revokeObjectURL(this.pdfUrl)
        this.pdfUrl = ''
      }
    },
    // Crea un customer a partir de los datos rellenados a mano (si no se eligió
    // uno guardado). Si ya existe uno con el mismo email, se reutiliza.
    // Los fallos no bloquean el pedido: se guarda igualmente sin asociar.
    async crearCustomerSiProcede() {
      const c = this.cliente
      if (c.id) return
      const tieneDatos = (c.nombre || '').trim() || (c.empresa || '').trim() || (c.email || '').trim()
      if (!tieneDatos) return

      const email = (c.email || '').trim().toLowerCase()
      if (email) {
        const existente = this.customers.find(x => (x.email || '').trim().toLowerCase() === email)
        if (existente) {
          c.id = existente.id
          return
        }
      }

      const info = {
        empresa: 'Harber London',
        companyName: c.empresa,
        nombre: c.nombre,
        apellidos: c.apellidos,
        nif: c.nif,
        vat: c.vat,
        email: c.email,
        telefono: c.telefono,
        direccion: c.direccion,
        cp: c.cp,
        ciudad: c.ciudad,
        pais: c.pais,
        web: c.web,
        redes: c.redes,
        envioActivo: this.envio.activo ? 1 : 0,
        envioNombre: this.envio.nombre,
        envioEmpresa: this.envio.empresa,
        envioTelefono: this.envio.telefono,
        envioDireccion: this.envio.direccion,
        envioCp: this.envio.cp,
        envioCiudad: this.envio.ciudad,
        envioPais: this.envio.pais,
        color: 'primary',
      }

      try {
        const { data } = await axios.post('/customers', info)
        if (data && data.id) {
          c.id = data.id
          this.customers.push({
            ...info,
            id: data.id,
            label: [c.nombre, c.apellidos].filter(Boolean).join(' ')
              + (c.empresa ? ` — ${c.empresa}` : ''),
          })
          this.notificar('Customer creado y asociado al pedido')
        }
      } catch (e) {
        // Sin bloqueo: el pedido se guarda sin customer asociado
      }
    },
    // Finaliza: si hay email envía el pedido, y siempre lo guarda en la BD
    async finalizar() {
      if (this.carrito.length === 0) return
      this.finalizando = true
      const revision = (this.emailRevision || '').trim()
      // El de revisión manda siempre; el del cliente solo si se marca "Enviar correo".
      const email = revision || (this.enviarCorreo ? (this.cliente.email || '').trim() : '')
      const tieneEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      let emailEnviado = false
      try {
        // 0) Si no se eligió un customer guardado pero se rellenaron sus datos,
        //    se crea el customer y el pedido queda asociado a él
        await this.crearCustomerSiProcede()

        // 1) Enviar email si hay correo válido.
        //    Si es un correo de revisión, NO se marca como enviado al cliente
        //    (así se puede reenviar al cliente después desde la lista).
        if (tieneEmail) {
          try {
            await axios.post('/escaner/pedido/email', { ...this.construirPayloadPedido(), email })
            emailEnviado = !revision
            this.notificar(revision ? `Enviado a revisión: ${email}` : `Pedido enviado a ${email}`)
          } catch (e) {
            this.notificar('No se pudo enviar el email, el pedido se guardará igualmente', 'danger')
          }
        }
        // 2) Guardar el pedido en la BD
        await axios.post('/escaner/pedidos', { ...this.construirPayloadPedido(), emailEnviado })
        this.notificar('Pedido guardado')
        this.resetPedido()
      } catch (e) {
        this.notificar('No se pudo guardar el pedido', 'danger')
      } finally {
        this.finalizando = false
      }
    },
    resetPedido() {
      this.carrito = []
      this.producto = null
      this.imagenActiva = ''
      this.descuento = 0
      this.vat0 = false
      this.datosBanco = false
      this.enviarCorreo = false
      this.emailRevision = ''
      this.customerSel = null
      this.cliente = {
        id: null,
        empresa: '', nombre: '', apellidos: '', nif: '', vat: '',
        email: '', telefono: '',
        direccion: '', cp: '', ciudad: '', pais: '',
        web: '', redes: '', notas: '', observaciones: '',
      }
      this.envio = {
        activo: false,
        empresa: '', nombre: '', telefono: '',
        direccion: '', cp: '', ciudad: '', pais: '',
      }
    },
  },
}
</script>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

.esc-cliente-resumen {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 1rem;
  background: rgba(115, 103, 240, 0.04);

  &__nombre {
    font-weight: 600;
  }
}

.dark-layout .esc-cliente-resumen {
  border-color: #3b4253;
  background: rgba(115, 103, 240, 0.08);
}
</style>

<style lang="scss" scoped>
$accent: #7367f0;

// Evita el zoom por doble toque en toda la página del escáner (iOS/iPad)
.escaner-page {
  touch-action: manipulation;
}

.esc-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  &__icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #7367f0, #9e95f5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-right: 1rem;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.35);
  }
}

.esc-total-badge {
  font-size: 0.95rem;
  padding: 0.5rem 0.9rem;
}

.esc-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #ebe9f1;
  min-width: 0;
}

.esc-scan-input {
  ::v-deep .form-control {
    font-size: 1.1rem;
    height: auto;
    padding-top: 0.7rem;
    padding-bottom: 0.7rem;
  }
  ::v-deep .input-group-text { background: #fff; }
}

.esc-reader-wrap {
  position: relative;
  border-radius: 10px;
  overflow: hidden;        // recorta arriba/abajo lo que sobre del vídeo
  background: #000;
  max-width: 320px;
  height: 200px;           // altura fija: solo se ve la banda central
  margin: 0 auto;

  // El vídeo se queda a su escala natural (el centro NO cambia)
  // y se centra verticalmente; lo que sobra arriba/abajo se recorta.
  ::v-deep #esc-reader {
    width: 100%;
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);

    video {
      width: 100% !important;
      height: auto !important;
      display: block;
    }
    // La región sombreada de html5-qrcode se desalinea al recortar el alto
    #qr-shaded-region { display: none !important; }
  }

  // Guía central para apuntar el código
  &::after {
    content: '';
    position: absolute;
    left: 12%;
    right: 12%;
    top: 50%;
    height: 2px;
    background: rgba(234, 84, 85, 0.85);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 2;
  }
}

.esc-last-scan {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  background: rgba(40, 199, 111, 0.92);
  color: #fff; font-weight: 600; text-align: center;
  padding: 0.4rem; font-size: 0.9rem;
}

/* --- Ficha producto --- */
.esc-product-card { border: 1px solid #d8d4f7; }

.esc-gallery-main {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f8f8fc;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img { width: 100%; height: 100%; object-fit: cover; }
  i { font-size: 2rem; }
}

.esc-variant {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 0.25rem;
  border-bottom: 1px solid #f3f2f7;

  &:last-child { border-bottom: none; }
  &.matched { background: rgba(115, 103, 240, 0.06); border-radius: 8px; }
}

.esc-variant-thumb {
  width: 40px; height: 40px; flex: 0 0 40px;
  border-radius: 6px; overflow: hidden; background: #f8f8fc; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
}

.esc-variant-info { flex: 1 1 auto; min-width: 0; }
.esc-variant-color { font-weight: 600; font-size: 0.85rem; }
.esc-variant-sku { font-size: 0.7rem; color: #b9b9c3; }
.esc-variant-wsp { font-weight: 700; min-width: 56px; text-align: right; }
.esc-variant-rrp { font-weight: 600; font-size: 0.68rem; color: #b9b9c3; margin-top: 2px; white-space: nowrap; }
.esc-variant-add {
  padding: 0.3rem 0.6rem;
  touch-action: manipulation; // evita el zoom por doble toque en iOS/iPad
  user-select: none;
}

/* --- Carrito --- */
.esc-empty {
  text-align: center; padding: 2.5rem 1rem 3rem; color: #b9b9c3;
  i { font-size: 2.5rem; opacity: 0.4; margin-bottom: 0.75rem; display: block; }
}

.esc-cart-list { padding: 0.5rem 0; }

.esc-cart-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1.25rem;
  border-bottom: 1px solid #f3f2f7;
  &:last-of-type { border-bottom: none; }
}

.esc-thumb {
  width: 48px; height: 48px; flex: 0 0 48px;
  border-radius: 8px; background: #f8f8fc; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }

  &.esc-thumb-clickable {
    cursor: pointer;
    transition: transform 0.15s;
    &:hover { transform: scale(1.06); }
  }
}

/* --- Aviso sutil (snackbar) --- */
.esc-snack {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: rgba(40, 199, 111, 0.96);
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.45rem 0.95rem;
  border-radius: 50px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  z-index: 2000;
  pointer-events: none;
}

.esc-snack-fade-enter-active,
.esc-snack-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.esc-snack-fade-enter,
.esc-snack-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}

/* --- Visor de PDF --- */
.esc-pdf-wrap {
  width: 100%;
  height: 75vh;
  background: #525659;
}
.esc-pdf-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}

.esc-img-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  background: #fff;

  img {
    max-width: 100%;
    max-height: 78vh;
    object-fit: contain;
  }
}

.esc-info { flex: 1 1 auto; min-width: 0; }
.esc-title {
  font-weight: 600; font-size: 0.9rem;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.esc-color { font-size: 0.78rem; color: #6e6b7b; margin-right: 0.4rem; }
.esc-barcode { font-size: 0.72rem; color: #b9b9c3; }

.esc-actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.esc-qty {
  flex: 0 0 auto;
}

.esc-qty-group {
  width: 118px;
}

.esc-qty-btn {
  padding: 0.25rem 0.55rem;
  touch-action: manipulation; // evita el zoom por doble toque en iOS/iPad
  user-select: none;
}

/* Oculta las flechas nativas del input number */
.esc-qty-input {
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.esc-line { flex: 0 0 auto; display: flex; align-items: center; gap: 0.4rem; }
.esc-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.esc-price-group {
  width: 118px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(34, 41, 47, 0.06);

  .input-group-text {
    background: #f4f3fb;
    border-color: #e6e4f0;
    color: #a49fc0;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    padding: 0 0.45rem;
    min-width: 32px;
    justify-content: center;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  .form-control {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
}

.esc-price-input {
  font-weight: 700;
  font-size: 0.9rem;
  padding: 0.25rem 0.5rem;
  color: #2b2b2b;
  border-color: #e6e4f0;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

  &:focus { border-color: #7367f0; box-shadow: none; }
}

// Precio ajustado a mano: se resalta en el morado de la app
.esc-price-input.esc-price-manual {
  color: #7367f0;
  border-color: #7367f0;
  background: #f6f5ff;
}
.esc-price-group:focus-within .input-group-text { border-color: #7367f0; }

.esc-line-total {
  font-size: 0.75rem;
  font-weight: 700;
  color: #6e6b7b;
  padding-right: 2px;
}

.esc-cart-total {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.9rem 1.25rem 0.3rem; font-weight: 600;
}

.esc-total-num { font-size: 1.35rem; font-weight: 700; color: $accent; }

.esc-sticky { position: sticky; top: 100px; }

.esc-summary-total { border-top: 1px solid #ebe9f1; padding-top: 0.75rem; font-size: 1.1rem; }

.esc-desc-input { width: 90px; }

.esc-finalizar { font-weight: 600; box-shadow: 0 6px 16px rgba(115, 103, 240, 0.4); }

@media (max-width: 991px) {
  .esc-sticky { position: static; }
}

@media (max-width: 575px) {
  .esc-cart-item {
    flex-wrap: wrap;
    row-gap: 0.6rem;
  }
  .esc-info { flex: 1 1 0; }
  /* La cantidad y el precio siempre ocupan su propia fila completa,
     independientemente de lo que ocupe el SKU */
  .esc-actions {
    flex: 1 1 100%;
    justify-content: space-between;
  }
}
</style>
